import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { saveAs } from 'file-saver';
import { BuildingFilterModel } from 'src/app/shared/models/building-filter.model';
import { BuildingModel } from '../../models/building.model';
import { ExcelTemplateType, LanguageType } from '../../models/enum.model';
import { LocalCacheModel } from '../../models/local-cache.model';
import { BuildingService } from '../../services/building.service';
import { LocalCacheService } from '../../services/local-cache.service';
import { LocalizationService } from '../../services/localization.service';
import { NotificationService } from '../../services/notification.service';
import {
  ClearBuildingCategories,
  FilterBuildings,
} from '../action/building.action';
import { BuildingMarkerGroup } from './../../../modules/buildings-map/buildings-map.component';
import {
  BuildingCategoryModel,
  ComplexBuildingCategoryModel,
} from './../../models/complex-building-category.model';
import { ComplexBuildingModel } from './../../models/complex-building.model';
import { LocalStorageKeys } from './../../models/local-storage-keys.model';
import { BuildingCategoryService } from './../../services/building-category.service';
import { FilterService } from './../../services/filter.service';
import {
  AddBuilding,
  ClearBuildings,
  ClearSelectedBuilding,
  EditBuilding,
  ExportBuildings,
  GetAllBuildingMarkers,
  GetBuilding,
  GetBuildingCategories,
  ImportBuildings,
  RemoveBuilding,
  SearchByStreet,
  SelectBuilding,
} from './../action/building.action';

export interface BuildingStateModel {
  buildings: BuildingModel[];
  buildingsTotal: number;
  selected?: BuildingModel;
  markers: BuildingMarkerGroup[];
  streetRecommendations: string[];
  filter?: BuildingFilterModel;
  categories: BuildingCategoryModel[];
  filteredMarkers: string[];
}

@State<BuildingStateModel>({
  name: 'buildingstate',
  defaults: {
    buildings: [],
    buildingsTotal: 0,
    markers: [],
    filteredMarkers: [],
    streetRecommendations: [],
    categories: [],
  },
})
@Injectable()
export class BuildingState {
  constructor(
    private buildingService: BuildingService,
    private store: Store,
    private filterService: FilterService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private buildingCategoryService: BuildingCategoryService,
    private localCacheService: LocalCacheService,
    private localizationService: LocalizationService
  ) {}

  @Selector()
  static filter(ctx: BuildingStateModel) {
    return ctx.filter;
  }

  @Selector()
  static streetRecommendations(ctx: BuildingStateModel) {
    return ctx.streetRecommendations;
  }

  @Selector()
  static markers(ctx: BuildingStateModel) {
    return ctx.markers;
  }

  @Selector()
  static filteredMarkers(ctx: BuildingStateModel) {
    return ctx.filteredMarkers;
  }

  @Selector()
  static selected(ctx: BuildingStateModel) {
    return ctx.selected;
  }

  @Selector()
  static buildings(ctx: BuildingStateModel) {
    return ctx.buildings;
  }

  @Selector()
  static buildingsTotal(ctx: BuildingStateModel) {
    return ctx.buildingsTotal;
  }

  @Selector()
  static categories(ctx: BuildingStateModel) {
    return ctx.categories;
  }

  @Action(FilterBuildings)
  async filterBuildings(
    ctx: StateContext<BuildingStateModel>,
    action: FilterBuildings
  ) {
    var data = new ComplexBuildingModel();

    data = await this.buildingService
      .getAll(action.params, action.filter)
      .toPromise();

    if (data) {
      ctx.patchState({
        buildings: data.data,
        buildingsTotal: data.total,
        filter: action.filter,
        filteredMarkers: data.markers,
      });
    }
  }

  @Action(GetAllBuildingMarkers)
  async getAllBuildingMarkers(
    ctx: StateContext<BuildingStateModel>,
    action: GetAllBuildingMarkers
  ) {
    var data = [];

    const cached = this.localCacheService.get(
      LocalStorageKeys.BuildingsMarkersKey +
        '-' +
        this.translateService.currentLang
    );
    if (cached) {
      data = JSON.parse(cached.value);
    } else {
      data = await this.buildingService.getAllMarkers().toPromise();

      const cacheValue = new LocalCacheModel(
        LocalStorageKeys.BuildingsMarkersKey +
          '-' +
          this.translateService.currentLang,
        JSON.stringify(data)
      );

      this.localCacheService.set(cacheValue);
    }

    ctx.patchState({
      markers: data,
    });
  }

  @Action(GetBuilding)
  async getBuilding(
    ctx: StateContext<BuildingStateModel>,
    action: GetBuilding
  ) {
    let data: BuildingModel;

    data = await this.buildingService.get(action.id).toPromise();

    if (data) {
      ctx.patchState({ selected: data });
    }
  }

  @Action(AddBuilding)
  async addBuilding(
    ctx: StateContext<BuildingStateModel>,
    action: AddBuilding
  ) {
    this.localCacheService.remove(LocalStorageKeys.DashboardKey);
    this.localCacheService.remove(LocalStorageKeys.BuildingsMarkersKey);

    var data = await this.buildingService.add(action.model).toPromise();

    if (data) {
      this.store.dispatch(
        new FilterBuildings(
          this.filterService.generateHttpParams(),
          new BuildingFilterModel(
            [],
            [],
            '',
            '',
            [],
            0,
            0,
            LanguageType[this.translateService.currentLang.toUpperCase()]
          )
        )
      );

      this.notificationService.info(
        this.localizationService.translate('added-building'),
        this.localizationService.translate('successfull-added-building')
      );
    } else {
      this.notificationService.error(
        this.localizationService.translate('error'),
        this.localizationService.translate('unsuccessfull-added-building')
      );
    }
  }

  @Action(EditBuilding)
  async editBuilding(
    ctx: StateContext<BuildingStateModel>,
    action: EditBuilding
  ) {
    this.localCacheService.remove(LocalStorageKeys.DashboardKey);
    this.localCacheService.remove(LocalStorageKeys.BuildingsMarkersKey);

    var data = await this.buildingService.update(action.model).toPromise();
    if (data) {
      this.store.dispatch(
        new FilterBuildings(
          this.filterService.generateHttpParams(),
          new BuildingFilterModel(
            [],
            [],
            '',
            '',
            [],
            0,
            0,
            LanguageType[this.translateService.currentLang.toUpperCase()]
          )
        )
      );

      this.notificationService.info(
        this.localizationService.translate('edited'),
        this.localizationService.translate('successfull-edited-building')
      );
    }
  }

  @Action(RemoveBuilding)
  async removeBuilding(
    ctx: StateContext<BuildingStateModel>,
    action: RemoveBuilding
  ) {
    this.localCacheService.remove(LocalStorageKeys.DashboardKey);
    this.localCacheService.remove(LocalStorageKeys.BuildingsMarkersKey);

    var data = await this.buildingService.remove(action.modelId).toPromise();

    if (data) {
      this.store.dispatch(
        new FilterBuildings(
          this.filterService.generateHttpParams(),
          new BuildingFilterModel(
            [],
            [],
            '',
            '',
            [],
            0,
            0,
            LanguageType[this.translateService.currentLang.toUpperCase()]
          )
        )
      );

      this.notificationService.info(
        this.localizationService.translate('remove'),
        this.localizationService.translate('successfull-remove-building')
      );
    }
  }

  @Action(ImportBuildings)
  async importBuildings(
    ctx: StateContext<BuildingStateModel>,
    action: ImportBuildings
  ) {
    this.localCacheService.remove(LocalStorageKeys.DashboardKey);
    this.localCacheService.remove(LocalStorageKeys.BuildingsMarkersKey);

    this.notificationService.info(
      this.localizationService.translate('import-loading'),
      this.localizationService.translate('import-take-a-while')
    );

    const data = await this.buildingService.import(action.model).toPromise();

    if (data) {
      this.store.dispatch(
        new FilterBuildings(
          this.filterService.generateHttpParams(),
          new BuildingFilterModel(
            [],
            [],
            '',
            '',
            [],
            0,
            0,
            LanguageType[this.translateService.currentLang.toUpperCase()]
          )
        )
      );

      this.notificationService.info(
        this.localizationService.translate('import-result'),
        `${this.localizationService.translate('added')} ${
          data.added
        }, ${this.localizationService.translate('updated')} ${
          data.updated
        }, ${this.localizationService.translate('failed')}: ${data.failed}`
      );
    }
  }

  @Action(ExportBuildings)
  async exportBuildings(
    ctx: StateContext<BuildingStateModel>,
    action: ExportBuildings
  ) {
    const data = await this.buildingService.export(action.model).subscribe(
      (blob) => {
        const fileName =
          ExcelTemplateType[action.model.exportType] +
          '_' +
          action.model.municipalityName;
        saveAs(blob, fileName);
        this.notificationService.info(
          this.localizationService.translate('export'),
          this.localizationService.translate('successfully-exported')
        );
      },
      (error) => {}
    );
  }

  @Action(SelectBuilding)
  async selectBuilding(
    ctx: StateContext<BuildingStateModel>,
    action: SelectBuilding
  ) {
    if (action.model) {
      ctx.patchState({ selected: action.model });
    }
  }

  @Action(ClearSelectedBuilding)
  async clearSelectedBuilding(
    ctx: StateContext<BuildingStateModel>,
    action: ClearSelectedBuilding
  ) {
    ctx.patchState({ selected: undefined });
  }

  @Action(ClearBuildings)
  async clearBuildings(
    ctx: StateContext<BuildingStateModel>,
    action: ClearBuildings
  ) {
    const data = await this.buildingService.clearAll().toPromise();

    if (data) {
      this.store.dispatch(
        new FilterBuildings(
          this.filterService.generateHttpParams(),
          new BuildingFilterModel(
            [],
            [],
            '',
            '',
            [],
            0,
            0,
            LanguageType[this.translateService.currentLang.toUpperCase()]
          )
        )
      );

      this.notificationService.info(
        this.localizationService.translate('clear-result'),
        this.localizationService.translate(`buildings-cleared`)
      );
    }
  }

  @Action(SearchByStreet)
  async searchByStreet(
    ctx: StateContext<BuildingStateModel>,
    action: SearchByStreet
  ) {
    const data = await this.buildingService
      .searchByStreet(action.searchStreet)
      .toPromise();

    if (data) {
      ctx.patchState({ streetRecommendations: data });
    }
  }

  @Action(GetBuildingCategories)
  async getBuildingCategories(ctx: StateContext<BuildingStateModel>) {
    let data = new ComplexBuildingCategoryModel();

    const cached = this.localCacheService.get(
      LocalStorageKeys.BuildingCategoryKey
    );
    if (cached) {
      data = JSON.parse(cached.value);
    } else {
      data = await this.buildingCategoryService.getAll().toPromise();

      const cacheValue = new LocalCacheModel(
        LocalStorageKeys.BuildingCategoryKey,
        JSON.stringify(data)
      );

      this.localCacheService.set(cacheValue);
    }

    if (data) {
      ctx.patchState({ categories: data.data });
    }
  }

  @Action(ClearBuildingCategories)
  async clearBuildingCategories(ctx: StateContext<BuildingStateModel>) {
    ctx.patchState({ categories: [] });
  }
}
